import React, { Fragment, useCallback, useEffect, useState } from "react";
import ClearancePriceDisplay from "../ClearancePriceDisplay/ClearancePriceDisplay";
import PriceDisplay from "../PriceDisplay/PriceDisplay";
import UnitOfMeasure from "../UnitOfMeasure/UnitOfMeasure";
import PurchaseControl from "../PurchaseControl/PurchaseControl";
import AddToCart from "../AddToCart/AddToCart";
import PostCodeUpdate from "../PostCodeUpdate/PostCodeUpdate";
import GstToggle from "../GstToggle/GstToggle";
import PriceTypeToggle from "../PriceTypeToggle/PriceTypeToggle";
import WishlistToggle from "../WishlistToggle/WishlistToggle";
import ModelViewerToggle from "../ModelViewerToggle/ModelViewerToggle";
import CompareWishlistContainer from "../CompareWishlistContainer/CompareWishlistContainer";
import AddToCartQuantity from "../AddToCartQuantity/AddToCartQuantity";
import {
  enableBranchSelectorWidget,
  onBranchSearch,
  onBranchSelect,
} from "../BranchSelector/branchSelectorUtils";
import isValidQuantity, {
  MAX_QUANTITY,
  MIN_QUANTITY,
} from "../../utils/isValidQuantity";
import {
  CREATIVE_GLASS_ONLY,
  PURCHASABLE,
} from "../PurchaseControl/PurchaseControlTypes";
import debounce from "debounce";
import StockAvailabilityPopup from "../StockAvailability/StockAvailabilityPopup";
import StoreFinderSearch from "../StoreFinder/StoreFinderSearch";
import EnquireForm from "../EnquireForm/EnquireForm";
import statusMap from "../StockAvailability/StockAvailabilityStatusMap";
import StockAvailabilityPill from "../StockAvailabilityPill/StockAvailabilityPill";
import BranchSelector from "../BranchSelector/BranchSelector";
import { MaxIcon } from "../Icons/MaxIcon";
import isMaxUser from "../../utils/isMaxUser";

const PriceBlockContainer = ({ state, useCase, templateContext }) => {
  const shouldAddToCartBeDisabled =
    !state.canAddToCart || !isValidQuantity(state.quantity);
  const showAddToCartQuantity =
    state.canAddToCart && templateContext.purchaseControl === PURCHASABLE;
  const selectedUOM =
    state.selectedUnitOfMeasure === templateContext.pack1Uom
      ? "PACK1"
      : "PACK2";
  let stockAvailabilityStatus = undefined;

  let productAvailableForPurchase = false;

  if (state.stockError === null && state.stockAvailability) {
    stockAvailabilityStatus = statusMap[state.stockAvailability].eventText;
  }

  const debounceStock = useCallback(
    debounce(
      (quantity, branchId) =>
        useCase.handleStockAvailabilityQuery(quantity, branchId),
      300
    ),
    []
  );

  const fetchClearancePrice = useCallback(
    (gstIncludedPrice) => useCase.handleClearancePriceRequest(gstIncludedPrice),
    []
  );

  const customOrderMessage =
    templateContext.customOrder &&
    "Delivery Estimate: " + templateContext.customOrder;

  if (state.showStockAvailability) {
    useEffect(() => {
      debounceStock(state.quantity, state.branchId);
    }, [state.quantity, state.branchId]);
  }

  if (templateContext.clearance && templateContext.userType !== "MAX_USER") {
    useEffect(() => {
      if (
        state.selectedUnitOfMeasure === templateContext.pack1Uom &&
        state.gstInclusivePrice
      ) {
        fetchClearancePrice(state.gstInclusivePrice);
      }
    }, [state.selectedUnitOfMeasure, state.gstInclusivePrice]);
  }

  const [isNz, setIsNz] = useState(false);
  const [isMax, setIsMax] = useState(false);
  const isNzAddCTAButtonMessageFeatureEnabled = true;

  useEffect(() => {
    if (window.location.host.indexOf(".co.nz") > -1) {
      setIsNz(true);
    }

    const isMaxUserType =
      isMaxUser(templateContext.userType) ||
      templateContext.purchaseControl === "MAX_ONLY";

    if (isMaxUserType) {
      setIsMax(true);
    }
  }, []);

  if (state.showStockAvailability) {
    productAvailableForPurchase =
      state.stockAvailability !== "NO_LONGER_AVAILABLE";
  } else {
    productAvailableForPurchase = PURCHASABLE;
  }

  useEffect(() => {
    window.addEventListener("searchBranch", onBranchSearch);

    const onStoreSelected = (event) =>
      onBranchSelect(event, useCase.handleSelectedBranch);
    window.addEventListener("storeSelected", onStoreSelected);

    const onPageShow = () => {
      document
        .querySelector("rg-max-store-finder-widget")
        .setAttribute("show", "false");
    };
    window.addEventListener("pageshow", onPageShow);

    return () => {
      window.removeEventListener("searchBranch", onBranchSearch);
      window.removeEventListener("storeSelected", onStoreSelected);
      window.removeEventListener("pageshow", onPageShow);
    };
  });

  return (
    <React.Fragment>
      <div className="pdp-prices-block pdp-main-block__section">
        {(state.showPriceTypeToggle || state.showGstToggle) &&
          productAvailableForPurchase && (
            <div className="pdp-prices-block__top">
              {state.showPriceTypeToggle && (
                <PriceTypeToggle
                  priceType={state.priceType}
                  onChange={(event) => {
                    useCase.handlePriceTypeToggle(event.priceType);
                  }}
                />
              )}
              {state.showGstToggle && (
                <GstToggle
                  gstType={state.gstType}
                  onChange={() => {
                    useCase.handleGstToggle();
                  }}
                />
              )}
            </div>
          )}
        {state.isPostCodeUpdateRequired ? (
          <PostCodeUpdate
            onUpdate={(event) => {
              useCase.handlePostCodeUpdate(event.postCode);
            }}
          />
        ) : (
          <PriceDisplay
            gstType={state.gstType}
            price={state.price}
            priceError={state.priceListError}
            priceLoading={state.isPriceListLoading}
            stockAvailability={state.stockAvailability}
            isPriceListLoading={state.isPriceListLoading}
            isMax={isMaxUser(templateContext.userType)}
            isStockLoading={state.isStockAvailabilityLoading}
          />
        )}
        {state.gstInclusivePrice && (
          <ClearancePriceDisplay
            price={state.oldGstInclusivePrice}
            priceLoading={state.isClearancePriceLoading}
            error={state.error}
          />
        )}
      </div>

      {(state.showUnitOfMeasure || showAddToCartQuantity) && (
        <div className="pdp-main-block__section pdp-main-block__section__uom-qty-block">
          {state.showUnitOfMeasure && productAvailableForPurchase && (
            <UnitOfMeasure
              unit1={templateContext.pack1Uom}
              unit2={templateContext.pack2Uom}
              selectedUnit={state.selectedUnitOfMeasure}
              onChange={(event) => {
                useCase.handleUnitOfMeasureChange(event.unitOfMeasure);
              }}
            />
          )}

          {showAddToCartQuantity && productAvailableForPurchase && (
            <AddToCartQuantity
              value={state.quantity}
              min={MIN_QUANTITY}
              max={MAX_QUANTITY}
              onDecrement={useCase.handleQuantityDecrement}
              onIncrement={useCase.handleQuantityIncrement}
              onChange={useCase.handleQuantityChange}
              errorMessage={state.quantityError}
            />
          )}

          {state.showStockAvailability &&
            templateContext.purchaseControl !== CREATIVE_GLASS_ONLY && (
              <StockAvailabilityPill
                stockAvailability={state.stockAvailability}
                loading={state.isStockAvailabilityLoading}
                stockError={state.stockError}
                isVisible={!state.quantityError}
              />
            )}
        </div>
      )}

      {!state.stockError && state.showBranchSelector && (
        <BranchSelector
          branchName={state.branchName}
          onClick={() =>
            enableBranchSelectorWidget(
              templateContext.productCode,
              state.quantity,
              selectedUOM,
              state.branchId
            )
          }
        />
      )}

      {!state.stockError && !state.showBranchSelector && (
        <StockAvailabilityPopup
          stockBranchesError={state.stockBranchesError}
          stockBranchesList={state.stockAvailabilityBranchesList}
          productName={templateContext.productTitle}
          loading={state.isStockAvailabilityBranchesLoading}
          onTriggered={() =>
            useCase.handleStockAvailabilityBranchesQuery(
              state.quantity,
              selectedUOM
            )
          }
          branchName={state.branchName}
          showBranchSelector={state.showStockAvailability}
        >
          <StoreFinderSearch
            suburbsList={state.suburbsList}
            onUpdated={(location) => useCase.handleSuburbQuery(location)}
            onSubmitted={(location) =>
              location
                ? useCase.handleStockAvailabilityBranchesQuery(
                  state.quantity,
                  selectedUOM,
                  location
                )
                : useCase.handleStockAvailabilityBranchesQuery(
                  state.quantity,
                  selectedUOM
                )
            }
          />
        </StockAvailabilityPopup>
      )}

      {templateContext.purchaseControl && (
        <PurchaseControl
          type={templateContext.purchaseControl}
          renderMaxOnly={() => (
            <Fragment>
              <span className="pdp-main-block__span--purchase-control-message">
                {customOrderMessage}
              </span>
              <span className="pdp-main-block__span--purchase-control-message">
                {isNz && isMax
                  ? "Available online to trade account holders in maX"
                  : "Available to Trade Account holders in maX"}
              </span>
              <a
                className={
                  isNz && isMax && isNzAddCTAButtonMessageFeatureEnabled
                    ? "pdp-main-block__btn pdp-main-block__btn--shop-in-max-nz"
                    : "pdp-main-block__btn pdp-main-block__btn--shop-in-max"
                }
                href={templateContext.maxProductUri}
              >
                Shop in{" "}
                {isNz && isMax && isNzAddCTAButtonMessageFeatureEnabled ? (
                  <span className="max-icon">
                    <MaxIcon />
                  </span>
                ) : (
                  "maX"
                )}
              </a>{" "}
              <br /> <br />
              {isNz && isMax && isNzAddCTAButtonMessageFeatureEnabled && (
                <a
                  className="pdp-main-block__btn pdp-main-block__btn--shop-in-max max-find-store"
                  href="/storefinder"
                >
                  {" "}
                  Find In Store{" "}
                </a>
              )}
            </Fragment>
          )}
          renderEnquireOnly={() => (
            <Fragment>
              <span className="pdp-main-block__span--purchase-control-message">
                {customOrderMessage}
              </span>
              <EnquireForm templateContext={templateContext} />
            </Fragment>
          )}
          renderCreativeGlassOnly={() => (
            <Fragment>
              <span className="pdp-main-block__span--purchase-control-message">
                {customOrderMessage}
              </span>
              <span className="pdp-main-block__span--purchase-control-message">
                Available in selected areas only
              </span>
              <a
                className="pdp-main-block__btn pdp-main-block__btn--selected-areas-only"
                href={templateContext.creativeGlassUri}
              >
                Check Availability
              </a>
            </Fragment>
          )}
          renderInstoreOnly={() => (
            <Fragment>
              <span className="pdp-main-block__span--purchase-control-message">
                {customOrderMessage}
              </span>
              <div className="pdp-main-block__btn pdp-main-block__btn--in-store-only">
                In-store only
              </div>
            </Fragment>
          )}
          renderTradeAccountOnly={() => (
            <Fragment>
              <span className="pdp-main-block__span--purchase-control-message">
                {customOrderMessage}
              </span>
              <span className="pdp-main-block__span--purchase-control-message">
                Available to Trade Account holders in maX
              </span>
              <div className="pdp-main-block__btn pdp-main-block__btn--trade-account-only">
                Trade account only
              </div>
            </Fragment>
          )}
          renderPurchasable={() =>
            productAvailableForPurchase && (
              <Fragment>
                <span className="pdp-main-block__span--purchase-control-message">
                  {customOrderMessage}
                </span>
                {state.isStockAvailabilityLoading && isMax === true ? (
                  <div></div>
                ) : (
                  <div className="pdp-main-block__section pdp-main-block__section--primary-btns">
                    <div className="pdp-main-block__btn--add-to-cart">
                      <AddToCart
                        priceType={state.priceType}
                        userType={templateContext.userType}
                        productCode={templateContext.productCode}
                        productTitle={templateContext.productTitle}
                        unitOfMeasure1={templateContext.pack1Uom}
                        unitOfMeasure2={templateContext.pack2Uom}
                        selectedUnitOfMeasure={state.selectedUnitOfMeasure}
                        cmpGstInclusivePrice={state.cmpGstInclusivePrice}
                        gstInclusivePrice={state.gstInclusivePrice}
                        quantity={state.quantity}
                        disabled={shouldAddToCartBeDisabled}
                        stockAvailabilityStatus={stockAvailabilityStatus}
                      />
                    </div>
                  </div>
                )}
              </Fragment>
            )
          }
        />
      )}
      {(templateContext.showWishlistBtn ||
        templateContext.showModelViewerBtn) && (
        <CompareWishlistContainer
          renderModelViewerToggle={() => <ModelViewerToggle />}
          renderWishlistToggle={() => (
            <WishlistToggle
              isInWishlist={state.isInWishlist}
              onChange={() => {
                useCase.handleWishlistToggle();
              }}
            />
          )}
          templateContext={templateContext}
        />
      )}
    </React.Fragment>
  );
};

export default PriceBlockContainer;